import { useMemo } from 'react';
import type { FeatureKey, Feature } from '@/shared/types/features';
import { FeatureSchema } from '@/shared/types/features';
import { APP_ROUTES, PUBLIC_ROUTES } from '@/shared/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';
import { NAVIGATION_ITEMS } from '@/shared/components/Layout/constants';
import { isPageEnabledForBrand } from '@/shared/routes/pageRegistry';
import { useBrandStore } from '@/shared/stores/useBrandStore';

const isValidFeature = (feature: unknown): feature is Feature => {
  return FeatureSchema.safeParse(feature).success;
};

export const useFeatures = () => {
  const { user } = useAuth();

  return useMemo(() => {
    const validFeatures = user?.features?.filter(isValidFeature) ?? [];

    const isFeatureEnabled = (featureKey: FeatureKey): boolean => {
      return validFeatures.find((f) => f.key === featureKey)?.enabled ?? false;
    };

    // bare FeatureKey treated as length-1 set, so single-key call sites are unaffected
    const isAnyFeatureEnabled = (
      featureKey: FeatureKey | readonly FeatureKey[]
    ): boolean => {
      const featureKeys = Array.isArray(featureKey) ? featureKey : [featureKey];
      return featureKeys.some(isFeatureEnabled);
    };

    return { isFeatureEnabled, isAnyFeatureEnabled, features: validFeatures };
  }, [user?.features]);
};

export const useDefaultRoute = (): string => {
  const { user } = useAuth();
  const brandId = useBrandStore((state) => state.brandId);
  const { isAnyFeatureEnabled } = useFeatures();

  return useMemo(() => {
    // Same per-brand narrowing as the sidebar, else the default route can land
    // the user on a page the rail no longer shows for the selected brand.
    const selectedBrand = user?.brands.find((b) => b.id === brandId);

    const firstAccessibleItem = NAVIGATION_ITEMS.find(
      (item) =>
        isAnyFeatureEnabled(item.accessFeatureKeys ?? item.featureKey) &&
        isPageEnabledForBrand(item, selectedBrand)
    );

    return firstAccessibleItem?.path ?? APP_ROUTES.NOT_FOUND;
  }, [user?.brands, brandId, isAnyFeatureEnabled]);
};

export const useHomeRoute = (): string => {
  const { user } = useAuth();
  const defaultRoute = useDefaultRoute();

  if (!user) {
    return PUBLIC_ROUTES.LANDING;
  }

  return defaultRoute === APP_ROUTES.NOT_FOUND
    ? APP_ROUTES.PROFILE
    : defaultRoute;
};
